import React, { useMemo, useState } from 'react';
import { Alert, Button, InputNumber, Modal, Select, Typography } from 'antd';
import { CalculatorOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { localeForLanguage } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import {
  asCommercialUnit,
  COMMERCIAL_PRICING_CONVERSION_UNITS,
  COMMERCIAL_UNITS,
  CommercialQuantityValue,
  convertCommercialQuantity,
  roundCommercialQuantity,
} from '../../../utils/commercialQuantity';
import type { AllocationUnit, ConversionSource } from '../../../utils/dimensionConversion';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import './CommercialQuantityEditor.css';

type EntrySide = 'physical' | 'pricing';
type ConversionMode = 'conversion' | 'none';
type RoundingDirection = 'down' | 'up' | null;

interface CommercialQuantityEditorProps extends ConversionSource {
  value: CommercialQuantityValue;
  onChange: (value: CommercialQuantityValue) => void;
  maxPhysicalQuantity: number;
  maxPricingQuantity: number;
  disabled?: boolean;
  compact?: boolean;
}

const positive = (value: unknown, fallback = 1) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const CommercialQuantityEditor: React.FC<CommercialQuantityEditorProps> = ({
  value,
  onChange,
  maxPhysicalQuantity,
  maxPricingQuantity,
  disabled = false,
  compact = false,
  width,
  length,
  height,
  thickness,
}) => {
  const { t, i18n } = useTranslation(['orders', 'common']);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<ConversionMode>('none');
  const [entrySide, setEntrySide] = useState<EntrySide>('physical');
  const [pricingUnit, setPricingUnit] = useState<AllocationUnit>('m2');
  const [plainUnit, setPlainUnit] = useState<AllocationUnit>('pcs');
  const [amount, setAmount] = useState(1);
  const [rounding, setRounding] = useState<RoundingDirection>(null);

  const dimensions = useMemo(() => ({ width, length, height, thickness }), [height, length, thickness, width]);
  const isConverted = value.physicalUnit !== value.pricingUnit;

  const openEditor = () => {
    const nextMode: ConversionMode = value.physicalUnit === 'pcs' && value.pricingUnit !== 'pcs'
      ? 'conversion'
      : 'none';
    setMode(nextMode);
    setEntrySide('physical');
    setPricingUnit(value.pricingUnit === 'm3' ? 'm3' : 'm2');
    setPlainUnit(asCommercialUnit(value.physicalUnit));
    setAmount(positive(value.physicalQuantity));
    setRounding(null);
    setOpen(true);
  };

  const baseResult = useMemo(() => {
    if (mode === 'none') {
      return {
        physicalQuantity: positive(amount),
        physicalUnit: plainUnit,
        pricingQuantity: positive(amount),
        pricingUnit: plainUnit,
        error: null,
        theoreticalPhysical: positive(amount),
      };
    }

    if (entrySide === 'physical') {
      const converted = convertCommercialQuantity(positive(amount), 'pcs', pricingUnit, dimensions);
      return {
        physicalQuantity: positive(amount),
        physicalUnit: 'pcs' as AllocationUnit,
        pricingQuantity: converted.value ?? 0,
        pricingUnit,
        error: converted.error,
        theoreticalPhysical: positive(amount),
      };
    }

    const converted = convertCommercialQuantity(positive(amount), pricingUnit, 'pcs', dimensions);
    return {
      physicalQuantity: converted.value ?? 0,
      physicalUnit: 'pcs' as AllocationUnit,
      pricingQuantity: positive(amount),
      pricingUnit,
      error: converted.error,
      theoreticalPhysical: converted.exactValue ?? 0,
    };
  }, [amount, dimensions, entrySide, mode, plainUnit, pricingUnit]);

  const result = useMemo(() => {
    if (mode !== 'conversion' || entrySide !== 'pricing' || !rounding || baseResult.error) {
      return baseResult;
    }

    const physicalQuantity = rounding === 'up'
      ? Math.ceil(baseResult.theoreticalPhysical)
      : Math.floor(baseResult.theoreticalPhysical);
    const converted = convertCommercialQuantity(physicalQuantity, 'pcs', pricingUnit, dimensions);
    return {
      ...baseResult,
      physicalQuantity,
      pricingQuantity: converted.value ?? 0,
      error: converted.error,
    };
  }, [baseResult, dimensions, entrySide, mode, pricingUnit, rounding]);

  const hasFractionalPieces = mode === 'conversion'
    && entrySide === 'pricing'
    && !baseResult.error
    && Math.abs(baseResult.theoreticalPhysical - Math.round(baseResult.theoreticalPhysical)) > 0.0001;
  const downPieces = Math.floor(baseResult.theoreticalPhysical);
  const upPieces = Math.ceil(baseResult.theoreticalPhysical);
  const downPricing = convertCommercialQuantity(downPieces, 'pcs', pricingUnit, dimensions).value;
  const upPricing = convertCommercialQuantity(upPieces, 'pcs', pricingUnit, dimensions).value;

  const formatQuantity = (quantity: number) => Number(quantity).toLocaleString(
    localeForLanguage(i18n.language),
    { maximumFractionDigits: 3 },
  );

  const amountMaximum = mode === 'conversion' && entrySide === 'pricing'
    ? maxPricingQuantity
    : maxPhysicalQuantity;
  const resultOutOfBounds = result.physicalQuantity > maxPhysicalQuantity
    || result.pricingQuantity > maxPricingQuantity;
  const canApply = !result.error
    && !resultOutOfBounds
    && result.physicalQuantity > 0
    && result.pricingQuantity > 0;

  const handleApply = () => {
    if (!canApply) return;
    onChange({
      physicalQuantity: roundCommercialQuantity(result.physicalQuantity),
      physicalUnit: result.physicalUnit,
      pricingQuantity: roundCommercialQuantity(result.pricingQuantity),
      pricingUnit: result.pricingUnit,
    });
    setOpen(false);
  };

  return (
    <>
      <div className={`commercial-quantity${compact ? ' commercial-quantity--compact' : ''}`}>
        {isConverted ? (
          <div className="commercial-quantity__chain">
            <div><span>{t('orders:unitConversion.physicalQuantity')}</span><strong>{formatQuantity(value.physicalQuantity)} {formatUnit(value.physicalUnit, i18n.language)}</strong></div>
            <RightOutlined />
            <div className="commercial-quantity__pricing"><span>{t('orders:unitConversion.pricingQuantity')}</span><strong>{formatQuantity(value.pricingQuantity)} {formatUnit(value.pricingUnit, i18n.language)}</strong></div>
          </div>
        ) : (
          <strong className="commercial-quantity__plain">
            {formatQuantity(value.physicalQuantity)} {formatUnit(value.physicalUnit, i18n.language)}
          </strong>
        )}
        {!disabled && (
          <Button type="link" size="small" icon={<CalculatorOutlined />} onClick={openEditor}>
            {isConverted ? t('orders:unitConversion.change') : t('orders:unitConversion.configure')}
          </Button>
        )}
      </div>

      <Modal
        open={open}
        width={720}
        title={t('orders:unitConversion.title')}
        okText={t('orders:unitConversion.apply')}
        cancelText={t('common:cancel')}
        okButtonProps={{ disabled: !canApply }}
        onOk={handleApply}
        onCancel={() => setOpen(false)}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary" className="commercial-quantity-modal__intro">
          {t('orders:unitConversion.description')}
        </Typography.Paragraph>

        <span className="commercial-quantity-modal__label">{t('orders:unitConversion.mode')}</span>
        <div className="commercial-quantity-modal__choices">
          <button type="button" className={mode === 'conversion' ? 'active' : ''} onClick={() => { setMode('conversion'); setRounding(null); }}>
            <strong>{t('orders:unitConversion.convert')}</strong><small>{t('orders:unitConversion.convertHelp')}</small>
          </button>
          <button type="button" className={mode === 'none' ? 'active' : ''} onClick={() => { setMode('none'); setRounding(null); }}>
            <strong>{t('orders:unitConversion.noConversion')}</strong><small>{t('orders:unitConversion.noConversionHelp')}</small>
          </button>
        </div>

        {mode === 'none' ? (
          <div className="commercial-quantity-modal__fields">
            <label><span>{t('orders:unitConversion.quantity')}</span><InputNumber min={0.001} max={maxPhysicalQuantity} precision={3} step={0.001} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} value={amount} onChange={(next) => setAmount(positive(next))} /></label>
            <label><span>{t('orders:unitConversion.unit')}</span><Select value={plainUnit} options={COMMERCIAL_UNITS.map((unit) => ({ value: unit, label: formatUnit(unit, i18n.language) }))} onChange={setPlainUnit} /></label>
          </div>
        ) : (
          <>
            <div className="commercial-quantity-modal__fields">
              <label><span>{t('orders:unitConversion.physicalUnit')}</span><Select disabled value="pcs" options={[{ value: 'pcs', label: formatUnit('pcs', i18n.language) }]} /></label>
              <label><span>{t('orders:unitConversion.pricingUnit')}</span><Select value={pricingUnit} options={COMMERCIAL_PRICING_CONVERSION_UNITS.map((unit) => ({ value: unit, label: formatUnit(unit, i18n.language) }))} onChange={(next) => { setPricingUnit(next); setRounding(null); }} /></label>
            </div>

            <span className="commercial-quantity-modal__label">{t('orders:unitConversion.entryQuestion')}</span>
            <div className="commercial-quantity-modal__choices">
              <button type="button" className={entrySide === 'physical' ? 'active' : ''} onClick={() => { setEntrySide('physical'); setAmount(positive(value.physicalQuantity)); setRounding(null); }}>
                <strong>{t('orders:unitConversion.enterPieces')}</strong><small>{t('orders:unitConversion.enterPiecesHelp')}</small>
              </button>
              <button type="button" className={entrySide === 'pricing' ? 'active' : ''} onClick={() => { setEntrySide('pricing'); setAmount(positive(value.pricingQuantity)); setRounding(null); }}>
                <strong>{t('orders:unitConversion.enterPricing', { unit: formatUnit(pricingUnit, i18n.language) })}</strong><small>{t('orders:unitConversion.enterPricingHelp')}</small>
              </button>
            </div>

            <label className="commercial-quantity-modal__amount">
              <span>{entrySide === 'physical' ? t('orders:unitConversion.piecesAmount') : t('orders:unitConversion.pricingAmount', { unit: formatUnit(pricingUnit, i18n.language) })}</span>
              <InputNumber min={0.001} max={amountMaximum} precision={3} step={0.001} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} value={amount} onChange={(next) => { setAmount(positive(next)); setRounding(null); }} />
            </label>

            {result.error ? (
              <Alert type="warning" showIcon message={t('orders:unitConversion.missingDimensions')} />
            ) : resultOutOfBounds ? (
              <Alert type="warning" showIcon message={t('orders:unitConversion.quantityLimit')} />
            ) : (
              <div className="commercial-quantity-modal__equation">
                <div><span>{t('orders:unitConversion.physicalQuantity')}</span><strong>{formatQuantity(result.physicalQuantity)} {formatUnit(result.physicalUnit, i18n.language)}</strong></div>
                <RightOutlined />
                <div><span>{t('orders:unitConversion.pricingQuantity')}</span><strong>{formatQuantity(result.pricingQuantity)} {formatUnit(result.pricingUnit, i18n.language)}</strong></div>
              </div>
            )}

            {hasFractionalPieces && (
              <div className="commercial-quantity-modal__rounding">
                <div className="commercial-quantity-modal__rounding-head"><strong>{t('orders:unitConversion.fractionalTitle')}</strong><span>{formatQuantity(baseResult.pricingQuantity)} {formatUnit(pricingUnit, i18n.language)} = {formatQuantity(baseResult.theoreticalPhysical)} {formatUnit('pcs', i18n.language)}</span></div>
                <p>{t('orders:unitConversion.fractionalDescription')}</p>
                <div className="commercial-quantity-modal__rounding-options">
                  <button type="button" disabled={downPieces <= 0} className={rounding === 'down' ? 'active' : ''} onClick={() => setRounding(rounding === 'down' ? null : 'down')}>
                    <span>{t('orders:unitConversion.roundDown')}</span><small>{formatQuantity(downPricing || 0)} {formatUnit(pricingUnit, i18n.language)}</small><strong>{downPieces} {formatUnit('pcs', i18n.language)}</strong>
                  </button>
                  <button type="button" className={rounding === 'up' ? 'active' : ''} onClick={() => setRounding(rounding === 'up' ? null : 'up')}>
                    <span>{t('orders:unitConversion.roundUp')}</span><small>{formatQuantity(upPricing || 0)} {formatUnit(pricingUnit, i18n.language)}</small><strong>{upPieces} {formatUnit('pcs', i18n.language)}</strong>
                  </button>
                </div>
                {!rounding && <p className="commercial-quantity-modal__rounding-optional">{t('orders:unitConversion.roundingOptional')}</p>}
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default CommercialQuantityEditor;
